import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TenantRiskService } from '../tenantRisk.service';
import { JurisdictionRiskConfigService } from '../Jurisdiction-risk-config.service';
import { LikelihoodColorLevel, ResolvedExposureBand, EMPTY_EXPOSURE_BAND } from '../../models/risk-assessment.model';
import { parseLikelihoodColorLevels } from '../../shared/utils/risk-level-parsing.util';
import { sanitizeCssColor } from '../../shared/utils/css-color.util';

/** Neutral grey shown for a %OEL value that falls outside every configured band (matches the gravimetric card). */
const DEFAULT_EXPOSURE_BAND_COLOR = '#828282';

/**
 * Loads the tenant / jurisdiction exposure-likelihood bands and bands a %OEL value against them for
 * report mappers. Parses the JSON blob through the shared {@link parseLikelihoodColorLevels} — the
 * one parser the Risk Register and report popups already share — so the levels come back sorted
 * ascending by numeric key with non-numeric keys dropped. That key order is load-bearing:
 * {@link resolveExposureBand} treats the last level as the catch-all for any value above its upper
 * bound, so an unsorted or non-numeric-keyed config would otherwise send high results into the wrong
 * band.
 */
@Injectable({ providedIn: 'root' })
export class ReportRiskLevelService {
    constructor(
        private tenantRiskService: TenantRiskService,
        private jurisdictionRiskConfigService: JurisdictionRiskConfigService
    ) { }

    async loadTenantRiskLevels(): Promise<LikelihoodColorLevel[]> {
        try {
            const tenantRisks = await firstValueFrom(this.tenantRiskService.getCurrent());
            return parseLikelihoodColorLevels(tenantRisks?.exposureLikelihoodLevels);
        } catch {
            return [];
        }
    }

    async loadJurisdictionRiskLevels(jurisdictionIds: string[]): Promise<Record<string, LikelihoodColorLevel[]>> {
        const map: Record<string, LikelihoodColorLevel[]> = {};
        const uniqueIds = [...new Set(jurisdictionIds.filter(id => id != null))];
        if (!uniqueIds.length) return map;

        try {
            const configs = await firstValueFrom(this.jurisdictionRiskConfigService.getByJurisdictionIds(uniqueIds));
            for (const config of configs || []) {
                if (!config?.exposureLikelihoodLevels) continue;
                map[config.jurisdictionId] = parseLikelihoodColorLevels(config.exposureLikelihoodLevels);
            }
        } catch {
            // jurisdiction config unavailable — degrade to the tenant base at the call site
        }

        return map;
    }

    /**
     * Bands a %OEL value against the jurisdiction's (or tenant's) exposure-likelihood levels and
     * returns the full resolved band. The last level is the catch-all for any value above its upper
     * bound. Returns an empty band (neutral colour) when the value matches no level or no config is
     * available — never a fabricated band label.
     */
    resolveExposureBand(
        percentage: number,
        jurisdictionId: string | undefined,
        tenantRiskLevels: LikelihoodColorLevel[],
        jurisdictionRiskLevelsMap: Map<string, LikelihoodColorLevel[]>
    ): ResolvedExposureBand {
        const riskLevels = this.getRiskLevelsForJurisdiction(jurisdictionId, tenantRiskLevels, jurisdictionRiskLevelsMap);
        if (!riskLevels.length) return { ...EMPTY_EXPOSURE_BAND, color: DEFAULT_EXPOSURE_BAND_COLOR };

        let matched: LikelihoodColorLevel | undefined;
        riskLevels.forEach((level, index) => {
            const start = Number(level.minQuantThreshold);
            const end = Number(level.maxQuantThreshold);
            if (percentage >= start && percentage <= end) {
                matched = level;
            }
            if (index === riskLevels.length - 1 && percentage > end) {
                matched = level;
            }
        });

        return matched
            ? this.toBand(matched)
            : { ...EMPTY_EXPOSURE_BAND, color: DEFAULT_EXPOSURE_BAND_COLOR };
    }

    /** Maps configured levels to the template band shape, preserving their ascending key order. */
    toBands(levels: LikelihoodColorLevel[]): ResolvedExposureBand[] {
        return levels.map(level => this.toBand(level));
    }

    private toBand(level: LikelihoodColorLevel): ResolvedExposureBand {
        return {
            key: Number.isFinite(level.key) ? level.key : null,
            name: level.Name ?? '',
            quantitativeName: level.QuantitativeName ?? '',
            color: sanitizeCssColor(level.Color) || DEFAULT_EXPOSURE_BAND_COLOR,
            min: Number.isFinite(Number(level.minQuantThreshold)) ? Number(level.minQuantThreshold) : null,
            max: Number.isFinite(Number(level.maxQuantThreshold)) ? Number(level.maxQuantThreshold) : null,
            minSamples: level.MinSamples != null && Number.isFinite(level.MinSamples) ? level.MinSamples : null,
            reassessmentFrequencyMonths: level.ReassessmentFrequencyMonths != null && Number.isFinite(level.ReassessmentFrequencyMonths) ? level.ReassessmentFrequencyMonths : null,
            description: level.Description ?? ''
        };
    }

    private getRiskLevelsForJurisdiction(
        jurisdictionId: string | undefined,
        tenantRiskLevels: LikelihoodColorLevel[],
        jurisdictionRiskLevelsMap: Map<string, LikelihoodColorLevel[]>
    ): LikelihoodColorLevel[] {
        if (jurisdictionId != null && jurisdictionRiskLevelsMap.has(jurisdictionId)) {
            return jurisdictionRiskLevelsMap.get(jurisdictionId) || tenantRiskLevels;
        }
        return tenantRiskLevels;
    }
}
